// California State University, Long Beach: public college page (#2739), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - CSULB's Common Data Set 2025-2026 (Institutional Research and
//    Analytics, "CDS 2025 - 2026 (PDF)"; its cover says "Common Data Set
//    2025-2026", while the page footers still carry the template's
//    "2024-2025"): C1 applicants, admits and enrollees for Fall 2025 with the
//    residency table, C8A (no use of SAT or ACT), C21/C22 (no early decision
//    or early action). Quirk: C1's applicant total by sex (88,276) and the
//    residency table's applicant total (88,224) differ by 52; the page shows
//    the C1 total and the residency rows as published. Admits (40,105) and
//    enrollees (5,884) agree in both tables.
//  - csulb.edu: Enrollment Services Key Dates and Deadlines (Fall 2027
//    Admission column), the First-Time, First-Year Student Application
//    Process, Admission Eligibility (minimum index, STEM index, auditions,
//    local preference) and CSULB Index (SAT and ACT not considered) pages.
//    The Admission Eligibility page's opening line still says applications
//    are accepted "October 1 through December 1"; the Key Dates page and the
//    High School Students page both give November 30, 2026 for Fall 2027,
//    so the page uses November 30.
//  - The CSU's 2027-2028 Admission Handbook (calstate.edu, read in a browser
//    because the site refuses plain downloads) for the a-g language and arts
//    wording. SJSU's CSU Eligibility Requirements page calls these the "CSU
//    update for 2027-28 application cycle".
//  - CSULB Financial Aid and Scholarships, Undergraduate Costs: the
//    2026-2027 cost of attendance for a student in campus housing, both
//    residency columns. The CDS's G1/G5 give the same 2026-2027 figures.
//  - College Scorecard API, school id 110583 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'CSULB Common Data Set 2025-2026',
        url: 'https://www.csulb.edu/sites/default/files/2026/documents/CDS%202025%20-%202026%20%28PDF%29_0.pdf',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'CSULB Enrollment Services, Key Dates and Deadlines',
        url: 'https://www.csulb.edu/enrollment-services/key-dates-and-deadlines',
        read: '2026-10-01',
    },
    process: {
        label: 'CSULB Admissions, First-Time, First-Year Student Application Process',
        url: 'https://www.csulb.edu/admissions/first-time-first-year-student-application-process',
        read: '2026-10-01',
    },
    eligibility: {
        label: 'CSULB Admissions, First-Time, First-Year Student Admission Eligibility',
        url: 'https://www.csulb.edu/admissions/first-time-first-year-student-admission-eligibility',
        read: '2026-10-01',
    },
    index: {
        label: 'CSULB Admissions, First-Time, First-Year Student CSULB Index',
        url: 'https://www.csulb.edu/admissions/first-time-first-year-student-csulb-index',
        read: '2026-10-01',
    },
    csuHandbook: {
        label: 'California State University, Admission Handbook 2027-2028',
        url: 'https://www.calstate.edu/attend/student-services/Documents/csu-admission-handbook.pdf',
        read: '2026-10-01',
    },
    costs: {
        label: 'CSULB Financial Aid and Scholarships, Undergraduate Costs',
        url: 'https://www.csulb.edu/financial-aid-and-scholarships/undergraduate-costs-0',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?110583-California-State-University-Long-Beach',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'california-state-university-long-beach',
    kbId: 'california_state_university_long_beach',
    name: 'California State University, Long Beach',
    shortName: 'Cal State Long Beach',
    aliases: ['CSULB', 'Long Beach State'],
    location: 'Long Beach, CA',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (CSULB College of Business branding); decoration only.
    branding: {
        primary: '#EBA91B',
        secondary: '#000000',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/california_state_university_long_beach_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 88276,
        admitted: 40105,
        enrolled: 5884,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 84462, admitted: 38399 },
            { group: 'Out-of-state', applied: 2123, admitted: 1005 },
            { group: 'International', applied: 1639, admitted: 701 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'CSULB\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision and early action',
        note: 'CSULB has no early decision or early action plan; the Common Data Set answers no to both. There is one Cal State Apply filing period.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Admission to CSULB is very competitive, and many eligible applicants are not offered a place. What each applicant needs:',
        rows: [
            { school: 'Choosing a major', requirement: 'You select a major or undeclared on the application. CSULB makes admission decisions by intended major only.', source: 'process' },
            { school: 'All majors', requirement: 'California residents and graduates of California high schools need at least a 2.5 a-g GPA and a CSULB Index of 3,200 to be considered; other applicants need a 3.0 GPA and an index of 3,500.', source: 'eligibility' },
            { school: 'STEM majors', requirement: 'Applicants are ranked by a STEM Index that puts more weight on quantitative skills, with a minimum of 3,600 to be considered. Admission is to a pre-major.', source: 'eligibility' },
            { school: 'How the indexes are built', requirement: 'The CSULB Index combines your college preparatory GPA with your math and English GPAs; the STEM Index uses your math and science GPAs.', source: 'index' },
            { school: 'Music and Dance', requirement: 'An audition is mandatory; eligible applicants are admitted on the faculty\'s evaluation of it.', source: 'eligibility' },
            { school: 'Nursing', requirement: 'Admission is to Pre-Nursing. You apply to the Nursing department for the major at the junior level.', source: 'eligibility' },
        ],
        note: 'Applicants from CSULB\'s local area (by high school of graduation) and military applicants are offered admission if they meet the minimum index for their major, or as undeclared if they meet the CSULB minimum but not the major\'s. Other applicants are ranked by index for the space available.',
        source: 'eligibility',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Application window', value: 'Oct 1 to Nov 30' },
        rows: [
            { plan: 'Cal State Apply', deadline: 'November 30', decision: 'Mid-February to April', binding: false },
        ],
        note: 'You apply through Cal State Apply. You can start working on the application on August 1, 2026; submission opens October 1, 2026, and the deadline is November 30, 2026. Decisions come by email from mid-February to April 2027, and the enrollment deposit is due May 1, 2027.',
        source: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-blind',
        blind: true,
        line: 'The CSU does not use the SAT or ACT for admission, and the tests are not considered in CSULB\'s decision. If you are offered admission, you may send official scores, which are used for placement.',
        source: 'index',
        cdsLine: 'The Common Data Set agrees: it answers no to using SAT or ACT scores in admission.',
        cdsWhere: 'section C8A',
    },

    // Changes already in effect for this cycle: the CSU's a-g update applies
    // from the 2027-28 application cycle, the one for fall 2027 entry.
    currentChanges: [
        { topic: 'Language', line: 'The CSU language requirement means two years, or through the second level, of the same language, or two one-year first-level courses. You may not mix semesters of different languages or use partial units to meet it.', source: 'csuHandbook' },
        { topic: 'Arts', line: 'The CSU arts requirement is one year-long course or two semester courses in dance, music, theatre, visual arts or interdisciplinary arts, both semesters finished before you graduate from high school.', source: 'csuHandbook' },
    ],
    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [6838, 6838] },
            { item: 'Nonresident tuition', amounts: [0, 12246] },
            { item: 'Mandatory fees', amounts: [1910, 1910] },
            { item: 'Housing (campus housing)', amounts: [12034, 12034] },
            { item: 'Food', amounts: [6520, 6520] },
            { item: 'Books, course materials, supplies and equipment', amounts: [1806, 1806] },
            { item: 'Transportation', amounts: [1386, 1386] },
            { item: 'Miscellaneous personal expenses', amounts: [3968, 3968] },
        ],
        totals: [34462, 46708],
        note: 'For a student in campus housing. CSULB\'s budget for a California resident is $28,860 living at home and $42,476 off campus.',
        source: 'costs',
        netPrice: {
            average: 9426,
            byIncome: [
                { income: '$0 to $30,000', amount: 6443 },
                { income: '$30,001 to $48,000', amount: 6576 },
                { income: '$48,001 to $75,000', amount: 7576 },
                { income: '$75,001 to $110,000', amount: 11462 },
                { income: 'Over $110,000', amount: 19919 },
            ],
            note: 'What first-year, full-time in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from CSULB\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
